/**
 * The lab's settings, the ones that change how the floor works rather than who
 * may do what. Section 2.3 of the system test handover.
 *
 * One setting so far: how a case leaves a station. `automatic` is the three
 * production routes the lab has always worked to (handoff 11.2); `manual` asks
 * at every Mark done which station takes the case next. Automatic stays the
 * default, because it is what every case on the floor was started under.
 */

export const ROUTING_MODES = ['automatic', 'manual'] as const;
export type RoutingMode = (typeof ROUTING_MODES)[number];

/**
 * Proof of work: whether the bench photographs the finished work at Mark done.
 * `off` asks nobody; `optional` offers it and may be skipped; `mandatory`
 * refuses a completion without one, at the API and not only on the screen.
 * Section 2 of the request of 13 September 2026.
 */
export const WORK_PROOF_MODES = ['off', 'optional', 'mandatory'] as const;
export type WorkProofMode = (typeof WORK_PROOF_MODES)[number];

export interface LabSettings {
  routing: RoutingMode;
  workProof: WorkProofMode;
  /**
   * When the lab's day ends, "HH:mm" on the lab's clock, or null for never.
   * Every clock still running is stopped then, and the staff report counts no
   * run past it. Request of 25 September 2026. Read tolerantly: an API from
   * before the setting existed leaves it out, and the screen reads that as
   * unknown rather than as off.
   */
  dayEndsAt: string | null;
  /**
   * When a clock started after the day end stops, "HH:mm", or null for no
   * separate cut-off. Section 17 of the review of 2 October 2026.
   */
  afterHoursEndsAt: string | null;
  /**
   * Whether a case at Final Check may be taken over, after the warning. Off
   * unless management switches it on. Section 9 of the request of
   * 5 October 2026. Optional, for a session from a build that does not send it,
   * which reads as off.
   */
  takeOverAtFinalCheck?: boolean;
  /** Sent back as If-Match on a save, section 7.9. */
  rowVersion: string;
}

export const DEFAULT_ROUTING: RoutingMode = 'automatic';
export const DEFAULT_WORK_PROOF: WorkProofMode = 'off';
/** Six in the evening, as the API seeds it, until the lab says otherwise. */
export const DEFAULT_DAY_ENDS_AT = '18:00';
/** Midnight: an evening clock stops then, until the lab says otherwise. */
export const DEFAULT_AFTER_HOURS_ENDS_AT = '00:00';

/** "HH:mm", twenty four hour clock: what the setting accepts. */
export const DAY_END_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export function isDayEnd(value: string): boolean {
  return DAY_END_PATTERN.test(value);
}

export function isRoutingMode(value: string): value is RoutingMode {
  return (ROUTING_MODES as readonly string[]).includes(value);
}

export function isWorkProofMode(value: string): value is WorkProofMode {
  return (WORK_PROOF_MODES as readonly string[]).includes(value);
}

/** Whether Mark done stops to offer a photograph of the finished work. */
export function asksForProof(mode: WorkProofMode): boolean {
  return mode !== 'off';
}

/** Whether the completion is refused without one. The server enforces this; the screen mirrors it. */
export function requiresProof(mode: WorkProofMode): boolean {
  return mode === 'mandatory';
}

/**
 * The three refusals around a proof of work, worded once. The server says
 * them, the mock says them the same way, and a screen renders whichever
 * arrives verbatim.
 */
export const WORK_PROOF_REFUSAL = {
  required: 'A photograph of the finished work is required before this case moves on.',
  startFirst: 'Start the work before photographing it.',
  switchedOff: 'Proof of work is switched off.',
} as const;

/**
 * Whether completing a case needs somebody to name the next station.
 *
 * A case off its own route always does, whatever the mode: a flagged or
 * reworked case has nothing to infer from, and guessing would send it where
 * nobody chose. In manual mode every case does. The job view asks, and the
 * server refuses a completion without one, from this same rule.
 */
export function mustChooseDestination(routing: RoutingMode, offRoute: boolean): boolean {
  return offRoute || routing === 'manual';
}
